import React, { useRef, useState, useMemo, useEffect } from 'react';
import tw, { styled } from 'twin.macro';
import { useDrop } from 'react-dnd';

import { AnimatePresence, motion } from '@sopranodesign/framer-motion';
import { useTheme } from '@sopranodesign/pulse-volt';

import { Node, NodeConnector } from './Nodes/index';
import nodeTypes from './Nodes/Types/index';
import AddStepNode from './Nodes/Types/MessageStep/AddStepNode';
import AddTransitionNode from './Nodes/Types/Transition/AddTransitionNode';
import { useCanvas } from '../hooks/index';
import AutoZoom from './AutoZoom';
import { GRID_GAP, NODE_WIDTH } from './Canvas/renders';
import ConnectorNewStep from './ConnectorNewStep';

const Container = styled(motion.svg)`
  ${tw`h-screen w-full h-full absolute`}
  pointer-events: none;
`;

const CanvasContainer = styled.div`
  ${tw`relative overflow-hidden flex flex-col items-start h-screen`}
  z-index: 0;
  cursor: ${props =>
    props.linking ? `default` : props.panning ? `grabbing` : `grab`};
`;

/**
 * The DOM layer contains invisible boxes that match the size and position of
 * the nodes rendered on the canvas, so that we can still make use of our dnd
 * library and drag and drop nodes using `onMouseUp` and `onMouseMove`
 * It also renders node blocks and the action / tooltip menu for editing nodes.
 *
 * - DOM elements need to be repainted and composited individually every time
 *   their position or size has changed. This quickly becomes an issue as
 *   conversations get larger. To combat this, we now render as little as few
 *   HTML elements as possible, and hide (debounce) these elements as soon as
 *   the canvas view is panned or zoomed. This is represented in state using the
 *   `showPreview` field.
 */
const Nodes = () => {
  const ref = useRef(null);
  const [node, setNode] = useState(null);
  const [canvasClickAdd, setCanvasClickAdd] = useState(false);

  const {
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    panX,
    panY,
    panning,
    scale,
    newLinkOffset,
    newLinkDestinationOffset,
    compareNodes,
    linking,
    setLinking,
    snapPoint,
    setSnapPoint,
    nodes,
    nodeSizes,
    handleCanvasRender,
    selectedNodes,
    setSelectedNodes
  } = useCanvas();

  const { grey } = useTheme();

  const [, drop] = useDrop({
    accept: 'BLOCK',
    drop: item => {
      if (['messageStep', 'transition', 'automatedStep'].includes(item.id)) {
        setNode(item);
      }
    },
    canDrop: item =>
      ['messageStep', 'transition', 'automatedStep'].includes(item.id)
  });

  const gridSnapX = x => {
    const handleNegative = x < 0 ? -1 : 1;
    x = x * handleNegative;
    const grid = (NODE_WIDTH + GRID_GAP) / 2;
    return Math.round(x / grid) * grid * handleNegative;
  };

  const gridSnapY = y => {
    const handleNegative = y < 0 ? -1 : 1;
    y = y * handleNegative;
    const grid = GRID_GAP;
    return Math.round(y / grid) * grid * handleNegative;
  };

  const newNodes = nodes.map(node => {
    node.x = gridSnapX(node.x);
    node.y = gridSnapY(node.y);
    return node;
  });

  const { AddNode } = useMemo(() => {
    if (!node) {
      return {};
    }

    return nodeTypes[node.id];
  }, [node]);

  const handleStartLink = node => () => {
    setLinking({
      from: compareNodes(linking.from, node) ? null : node,
      to: null
    });
  };

  const [wizardActive, setWizardActive] = useState(false);

  // Here is where the step is created when using the connector lines
  const handleCanvasClick = () => {
    if (!linking.from) {
      return;
    }
    setCanvasClickAdd(true);
  };

  const handleEndLink = node => event => {
    event.stopPropagation();
    if (linking.from && linking.to) {
      return;
    }

    if (!linking.from || compareNodes(node, linking.from)) {
      return;
    }

    setLinking(link => ({
      from: link.from,
      to: node
    }));
  };

  const handleClose = () => {
    setNode(null);
    handleCanvasRender();
  };

  const onSave = node => {
    if (wizardActive) {
      setLinking(link => ({
        from: link.from,
        to: {
          base: node,
          id: node.id
        }
      }));
    }
    handleClose();
  };

  const modalOpen = AddNode || (linking.from && linking.to);

  // Cancel the link if the modal is closed
  useEffect(() => {
    if (!modalOpen) {
      setLinking({ from: null, to: null });
    }
  }, [modalOpen, setLinking]);

  const handleNewStepClose = () => {
    setCanvasClickAdd(false);
    setLinking({ from: null, to: null });
  };

  const submitNewStep = stepId => {
    setCanvasClickAdd(false);
    setNode({
      id: stepId,
      AddNode: AddStepNode
    });
    setWizardActive(true);
  };

  const onShiftClick = (type, id) => {
    if (
      !selectedNodes.find(
        selectedNode => selectedNode.id === id && selectedNode.nodeType === type
      )
    ) {
      setSelectedNodes([...selectedNodes, { id: id, nodeType: type }]);
    } else {
      setSelectedNodes(
        selectedNodes.filter(selectedNode => {
          if (selectedNode.id === id && selectedNode.nodeType === type) {
            return null;
          }
          return selectedNode;
        })
      );
    }
  };

  drop(ref);

  return (
    <>
      <AnimatePresence>
        {canvasClickAdd && (
          <ConnectorNewStep
            handleClose={handleNewStepClose}
            submitNewStep={submitNewStep}
          />
        )}
        {AddNode && (
          <AddNode handleClose={handleClose} onSave={onSave} type={node.id} />
        )}
        {linking.from && linking.to && (
          <AddTransitionNode
            handleClose={() => {
              handleCanvasRender();
              setLinking({ from: null, to: null });
            }}
            link={linking}
          />
        )}
      </AnimatePresence>
      <CanvasContainer
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseUp}
        onClick={handleCanvasClick}
        linking={linking.from}
        panning={panning}
        ref={ref}>
        <Container>
          <motion.g
            style={{
              scale,
              x: panX,
              y: panY
            }}>
            {newNodes && linking.from && !linking.to && (
              <NodeConnector snapPoint={snapPoint} />
            )}
          </motion.g>
        </Container>
        <AutoZoom />
        <motion.div
          style={{
            scale,
            originX: 'top',
            originY: 'left',
            x: panX,
            y: panY,
            position: 'absolute'
          }}>
          {newNodes.map((node, key) => (
            <Node
              key={key}
              node={node}
              x={node.x}
              y={node.y}
              onShiftClick={onShiftClick}
              selectedNodes={selectedNodes}
            />
          ))}
          {newNodes
            .filter(node => node.type === 'step')
            .map((node, key) => {
              const nodeSize = nodeSizes?.find(
                info => info.id === node.id && info.type === node.type
              );

              if (!nodeSize) {
                return;
              }

              return (
                <React.Fragment key={key}>
                  {nodeSize && (
                    <>
                      <motion.button
                        aria-label="Add new step"
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          cursor: 'pointer',
                          height: 16,
                          width: 16,
                          borderRadius: '25px',
                          x: node.x + newLinkOffset(node) - 8,
                          y: node.y + nodeSize.height / 2 - 1 - 8,
                          zIndex: 50,
                          border: 0
                        }}
                        whileHover={{
                          backgroundColor: grey,
                          opacity: 1,
                          height: 16,
                          width: 16
                        }}
                        onClick={handleStartLink(node)}
                        type="button"
                      />
                      <motion.button
                        aria-label="Add new step"
                        onClick={handleEndLink(node)}
                        onMouseEnter={() =>
                          setSnapPoint({
                            x: node.x + newLinkDestinationOffset(node),
                            y: node.y - nodeSize.height / 2 + 1
                          })
                        }
                        onMouseLeave={() => setSnapPoint(null)}
                        style={{
                          position: 'absolute',
                          height: 30,
                          width: 30,
                          opacity: 0,
                          x: node.x + newLinkDestinationOffset(node) - 15,
                          y: node.y - nodeSize.height / 2 + 1 - 15,
                          zIndex: 50
                        }}
                        type="button"
                      />
                    </>
                  )}
                </React.Fragment>
              );
            })}
        </motion.div>
      </CanvasContainer>
    </>
  );
};

export default Nodes;
